// src/global-ref.ts
var GlobalRef = class {
  constructor(uniqueName) {
    this.sym = Symbol.for(uniqueName);
  }
  get value() {
    return globalThis[this.sym];
  }
  set value(value) {
    ;
    globalThis[this.sym] = value;
  }
};

// src/helpers.ts
import slugify from "slugify";
import Sqids from "sqids";
var localesMap = { en: "en-us", nb: "no", psaccent: "en-us" };
function getZendeskLocale(locale) {
  const lower = locale.toLowerCase();
  return localesMap[lower] || lower;
}
function isArticleDraft(article) {
  return article.labels.includes("draft");
}
function isArticleUnsearchable(article) {
  return article.labels.includes("nosearch");
}
function findArticle(id, context) {
  const article = context.articles.find(
    (a) => a.slug === id || a.id === id || a.id === parseInt(id)
  );
  if (article == null ? void 0 : article.body) {
    return article;
  }
}
function getPartialCategories(categories) {
  return categories.map((c) => ({
    id: c.id,
    name: c.name,
    description: c.description,
    slug: c.slug,
    href: c.href,
    sections: getPartialSections(c.sections)
  }));
}
function getPartialSections(sections) {
  return sections.map((s) => ({
    id: s.id,
    name: s.name,
    description: s.description,
    href: s.href,
    slug: s.slug,
    articles: getPartialArticles(s.articles)
  }));
}
function getPartialArticles(articles) {
  return articles.map((a) => ({
    id: a.id,
    title: a.title,
    slug: a.slug,
    href: a.href,
    snippet: a.snippet
  }));
}
function getCategoryAndSectionByArticleId(id, categories) {
  for (let i = 0; i < categories.length; i++) {
    const category = categories[i];
    for (let j = 0; j < category.sections.length; j++) {
      const section = category.sections[j];
      const articleIds = section.articles.map((a) => a.id);
      if (articleIds.includes(id)) {
        return { category, section };
      }
    }
  }
  return {};
}
function slugifyText(text) {
  const sanitizedText = text.replace(/&quot;/g, "");
  return slugify(sanitizedText, { lower: true, remove: /[&*+~.()'"!:@?/]/g });
}
function findArticles(ids, articles) {
  const matches = [];
  for (let i = 0; i < articles.length; i++) {
    const article = articles[i];
    if (ids.includes(article.id)) {
      matches.push(article);
    }
    if (ids.length === matches.length) {
      return matches;
    }
  }
  return matches;
}
var platformsMap = /* @__PURE__ */ new Map([
  ["web", "web"],
  ["macOS", "macOS"],
  ["windows 10", "windows10"],
  ["android", "android"],
  ["iOS", "iOS"],
  ["linux", "linux"],
  ["plugin", "plugin"]
]);
var plansMap = /* @__PURE__ */ new Map([
  ["td-free", "free"],
  ["td-premium", "pro"],
  ["td-pro", "pro"],
  ["td-business", "business"],
  ["td-team", "team"]
]);
function parseArticle(json) {
  const slug = slugifyText(json.title);
  const labels = json.label_names;
  const platforms = [];
  const plans = [];
  let voteEnabled = false;
  for (const label of labels) {
    const tentativePlatform = platformsMap.get(label);
    if (tentativePlatform) {
      platforms.push(tentativePlatform);
      continue;
    }
    const tentativePlan = plansMap.get(label);
    if (tentativePlan) {
      plans.push(tentativePlan);
      continue;
    }
    if (label === "vote-enabled") {
      voteEnabled = true;
    }
  }
  return {
    id: json.id,
    slug,
    title: json.title,
    body: json.body,
    labels,
    plans,
    platforms,
    sectionId: json.section_id,
    href: "/help/articles/" + getArticlePath(slug, json.id),
    snippet: json.snippet || null,
    voteSum: json.vote_sum,
    voteCount: json.vote_count,
    voteEnabled
  };
}
function getIdNumericFromQueryParam(id) {
  const [idStr] = id.split("-");
  return parseInt(idStr);
}
function findCategory(id, categories) {
  const idNum = getIdNumericFromQueryParam(id);
  return categories.find((c) => c.slug === id || c.id === idNum);
}
function findCategoryWithSection(id, categories) {
  const idNum = getIdNumericFromQueryParam(id);
  return categories.find((c) => {
    const sectionIds = c.sections.reduce((acc, section) => {
      acc.push(section.id);
      acc.push(section.slug);
      return acc;
    }, []);
    return sectionIds.includes(id) || sectionIds.includes(idNum);
  });
}
function findSectionInCategory(id, category) {
  const idNum = getIdNumericFromQueryParam(id);
  return category.sections.find((s) => s.slug === id || s.id === idNum);
}
function resolveCategoryAndSection(categories, categoryId, sectionId) {
  let category;
  let section;
  if (categoryId) {
    category = findCategory(categoryId, categories);
  }
  if (sectionId) {
    if (!category) {
      category = findCategoryWithSection(sectionId, categories);
    }
    if (category) {
      section = findSectionInCategory(sectionId, category);
    }
  } else if (category) {
    const [firstSection] = category.sections;
    section = firstSection;
  }
  if (!category) {
    category = categories[0];
  }
  if (!section) {
    section = category.sections[0];
  }
  return { category, section };
}
function isPositiveInteger(v) {
  if (v) {
    const i = parseInt(v, 10);
    return i > 0 && i.toString() === v;
  } else {
    return false;
  }
}
var sqids = new Sqids({
  alphabet: "RPX2Fm9xKEM6nvBU1ytGgrluwIk3fb5oNJjODsdaczWASYCH04Z8eThVi7QLqp",
  minLength: 5
});
function resolveArticleIdFromPath(path) {
  if (isPositiveInteger(path)) {
    const id = parseInt(path, 10);
    return id;
  } else if (path.length >= 5) {
    const parts = path.split("-");
    const hashedId = parts[parts.length - 1];
    try {
      const ids = sqids.decode(hashedId);
      if (ids.length) {
        const id = ids[0];
        if (sqids.encode([id]) === hashedId) {
          return id;
        }
      }
    } catch (e) {
    }
    return null;
  } else {
    return null;
  }
}
function getArticlePath(slug, id) {
  return `${slug}-${sqids.encode([id])}`;
}

// src/api.ts
var CustomFieldTagsID = 360019952939;
var CustomFieldPlatformID = 360000107109;
var CustomFieldPlanID = 360011792059;
var CustomFieldTopicID = 360011763020;
var CustomFieldSubTopicID = 360011733419;
var CustomFieldUserID = 360011789999;
var defaultLocale = "en-us";
var usersCache = new GlobalRef("help-center-utils.users");
usersCache.value = [];
var externalIdsMap = new GlobalRef("help-center-utils.externalIds");
externalIdsMap.value = /* @__PURE__ */ new Map();
var Api = class {
  constructor(apiUrl, token, locale = defaultLocale) {
    this.apiUrl = apiUrl;
    const reqToken = Buffer.from(`support@doist.com/token:${token}`).toString("base64");
    this.auth = { Authorization: `Basic ${reqToken}` };
    this.locale = getZendeskLocale(locale);
  }
  async getArticle(id, locale = this.locale) {
    const url = `${this.apiUrl}/help_center/${locale}/articles/${id}`;
    let response = await fetch(url, { headers: this.auth });
    if (response.status === 404 && locale !== defaultLocale) {
      const enUrl = this.apiUrl + `/help_center/en-us/articles/${id}`;
      response = await fetch(enUrl, { headers: this.auth });
    }
    const json = await response.json();
    return parseArticle(json.article);
  }
  async searchArticles(searchString, locale = this.locale) {
    const encodedSearch = encodeURIComponent(searchString);
    const url = `${this.apiUrl}/help_center/articles/search?query=${encodedSearch}&locale=${locale}`;
    try {
      const response = await fetch(url);
      if (response.status === 200) {
        const json = await response.json();
        const articles = json.results.map(parseArticle);
        return getPartialArticles(
          articles.filter((a) => !isArticleDraft(a) && !isArticleUnsearchable(a))
        );
      }
    } catch (error) {
      console.error("Unable to search article:", error);
    }
    return [];
  }
  async rateArticle(rating) {
    const url = `${this.apiUrl}/help_center/articles/` + rating.articleId + "/" + (rating.positive ? "up" : "down");
    const params = { article_id: rating.articleId, user_id: rating.userId };
    const result = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...this.auth
      },
      body: JSON.stringify(params)
    });
    const json = await result.json();
    if (rating.reason) {
      const baseUrl = this.apiUrl.includes("todoist.zendesk.com") ? "https://todoist.com" : "https://twist.com";
      const articleUrl = `${baseUrl}/help/articles/${rating.articleId}`;
      const subject = (rating.positive ? "\u{1F44D}" : "\u{1F44E}") + ` \u{1F4D6} ${rating.articleTitle} (${articleUrl})`;
      return await this.createRequest(
        rating.email,
        subject,
        rating.reason,
        this.locale,
        rating.userId,
        "article_feedback",
        void 0,
        void 0,
        void 0,
        void 0
      );
    }
    return json.vote;
  }
  async createRequest(email, subject, body, lang, userId, category, subCategory, plan, uploads, clientInfo) {
    const locale = getZendeskLocale(lang);
    const customFields = [
      { id: CustomFieldTopicID, value: category }
    ];
    if (subCategory) {
      customFields.push({ id: CustomFieldSubTopicID, value: subCategory });
    }
    if (plan)
      customFields.push({ id: CustomFieldPlanID, value: plan });
    if (clientInfo)
      customFields.push({ id: CustomFieldPlatformID, value: clientInfo });
    if (userId)
      customFields.push({ id: CustomFieldUserID, value: userId.toString() });
    const tags = ["contact_form"];
    if (!userId) {
      tags.push("non_user");
    }
    customFields.push({ id: CustomFieldTagsID, value: tags });
    const params = {
      request: {
        subject,
        comment: { body, uploads },
        custom_fields: customFields,
        requester: { name: email, email, locale },
        via: {
          channel: "contact-form"
        }
      }
    };
    const url = `${this.apiUrl}/requests.json`;
    const result = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...this.auth
      },
      body: JSON.stringify(params)
    });
    const json = await result.json();
    return json.request.id;
  }
  async getUserZendeskId(userId) {
    if (!externalIdsMap.value.has(userId)) {
      const users = await this.fetch(`/users?external_id=${userId}`);
      if (users.count === 0) {
        return null;
      } else {
        externalIdsMap.value.set(userId, users.users[0].id);
      }
    }
    return externalIdsMap.value.get(userId);
  }
  async getUserRequests(userId) {
    const zendeskId = await this.getUserZendeskId(userId);
    let requests = [];
    if (zendeskId) {
      const json = await this.fetch(
        `/users/${zendeskId}/tickets/requested?sort_by=created_at&sort_order=desc`
      );
      requests = json.tickets;
    } else {
      const json = await this.fetch(
        `/search.json?query=custom_field_${CustomFieldUserID}:${userId}`
      );
      requests = json.results;
    }
    for (let i = 0; i < requests.length; i++) {
      const assigneeId = requests[i].assignee_id;
      if (assigneeId) {
        const assignee = await this.getUser(assigneeId);
        requests[i].assignee = assignee;
      }
    }
    return requests;
  }
  async getUserRequestsByCustomField(userId) {
    const url = `${this.apiUrl}/search.json?query=custom_field_${CustomFieldUserID}:${userId}`;
    const response = await fetch(url, { headers: this.auth });
    const json = await response.json();
    const requests = json.results;
    for (let i = 0; i < requests.length; i++) {
      const assigneeId = requests[i].assignee_id;
      if (assigneeId) {
        const assignee = await this.getUser(assigneeId);
        requests[i].assignee = assignee;
      }
    }
    return requests;
  }
  async getTicketComments(ticketId) {
    const url = `${this.apiUrl}/tickets/${ticketId}/comments.json?include_inline_images=true&include=users`;
    const response = await fetch(url, { headers: this.auth });
    const json = await response.json();
    usersCache.value.push(...json.users);
    const comments = json.comments;
    const publicComments = comments.filter((c) => c.public);
    for (let i = 0; i < publicComments.length; i++) {
      publicComments[i].author = await this.getUser(publicComments[i].author_id);
    }
    return publicComments;
  }
  async createTicketComment(ticketId, userId, comment, uploads) {
    const url = `${this.apiUrl}/tickets/${ticketId}.json`;
    const payload = { ticket: { comment: { body: comment, uploads, author_id: userId } } };
    await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...this.auth
      },
      body: JSON.stringify(payload)
    });
    const comments = await this.getTicketComments(ticketId);
    return comments[comments.length - 1];
  }
  async searchUser(query) {
    const url = `${this.apiUrl}/users/search.json?query=${query}`;
    const response = await fetch(url, { headers: this.auth });
    const json = await response.json();
    return json.users;
  }
  async getUser(userId) {
    const cachedUser = usersCache.value.find((u) => u.id === userId);
    if (cachedUser) {
      return cachedUser;
    }
    const response = await this.fetch(`/users/${userId}`);
    const user = response.user;
    usersCache.value.push(user);
    return user;
  }
  async addAttachment(filename, type, length, buffer) {
    const url = `${this.apiUrl}/uploads.json?filename=${filename}`;
    const result = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": type,
        "Content-Length": length,
        ...this.auth
      },
      body: buffer
    });
    const json = await result.json();
    return json.upload.token;
  }
  async getRelatedArticles(article, inContext, count = 4) {
    const relatedArticleIds = article.labels.map((label) => {
      if (label.includes("related-")) {
        const idStr = label.replace(/related-/, "");
        const id = parseInt(idStr, 10);
        if (!isNaN(id)) {
          return id;
        }
      }
      return void 0;
    }).filter(Boolean);
    const relatedArticles = findArticles(relatedArticleIds, inContext.articles);
    if (relatedArticles.length < count) {
      const results = await this.searchArticles(article.title);
      const missing = count - relatedArticles.length;
      relatedArticles.push(...results.slice(0, missing));
    }
    return relatedArticles;
  }
  async fetch(path, options = {}, authenticated = true) {
    const url = `${this.apiUrl}${path}`;
    if (authenticated) {
      options.headers = {
        ...options.headers,
        ...this.auth
      };
    }
    const result = await fetch(url, options);
    const data = await result.json();
    return data;
  }
};
function initializeZendeskApi(language = "en") {
  const { NEXT_PUBLIC_ZENDESK_API_URL, ZENDESK_API_URL, ZENDESK_API_TOKEN } = process.env;
  return new Api(NEXT_PUBLIC_ZENDESK_API_URL || ZENDESK_API_URL, ZENDESK_API_TOKEN, language);
}

// src/s3.ts
import { Readable } from "stream";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
var Product = /* @__PURE__ */ ((Product2) => {
  Product2["Todoist"] = "todoist";
  Product2["Twist"] = "twist";
  Product2["Test"] = "test";
  return Product2;
})(Product || {});
async function fetchNewContentFromS3(product, lastModified) {
  const s3 = new S3Client({
    region: "us-east-1",
    forcePathStyle: true
  });
  try {
    const { Body, LastModified } = await s3.send(
      new GetObjectCommand({
        Bucket: "doist-help-center-cache",
        Key: `main-${product}-help-center.json`,
        IfModifiedSince: lastModified
      })
    );
    if (Body instanceof Readable) {
      const contents = await streamToString(Body);
      return { content: JSON.parse(contents), lastModified: LastModified };
    }
  } catch (error) {
    if (error.name === "304") {
      return void 0;
    }
  }
  throw new Error("Unable to fetch Help Center contents from S3.");
}
async function streamToString(stream) {
  return await new Promise((resolve, reject) => {
    const chunks = [];
    stream.on("data", (chunk) => chunks.push(chunk));
    stream.on("error", reject);
    stream.on("end", () => resolve(Buffer.concat(chunks).toString("utf-8")));
  });
}
export {
  Api,
  CustomFieldPlanID,
  CustomFieldPlatformID,
  CustomFieldSubTopicID,
  CustomFieldTagsID,
  CustomFieldTopicID,
  CustomFieldUserID,
  Product,
  fetchNewContentFromS3,
  findArticle,
  findArticles,
  getArticlePath,
  getCategoryAndSectionByArticleId,
  getPartialArticles,
  getPartialCategories,
  getPartialSections,
  getZendeskLocale,
  initializeZendeskApi,
  isArticleDraft,
  isArticleUnsearchable,
  parseArticle,
  resolveArticleIdFromPath,
  resolveCategoryAndSection,
  slugifyText
};
